import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    ArrowLeft,
    ChevronDown,
    Crosshair,
    Eraser,
    Hand,
    Keyboard,
    Loader2,
    LogIn,
    LogOut,
    RefreshCw,
    Smartphone,
    X,
} from 'lucide-react';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const STRATEGY_LABELS = {
    'accessibility-id': 'accessibility-id',
    id: 'id (resource-id)',
    xpath: 'xpath',
    text: 'text',
    'class-name': 'class-name',
    'android-uiautomator': 'android-uiautomator',
};

// Pending session-teardown timer, kept at MODULE scope (not per-instance) so a
// quick close+reopen — or any remount — cancels a stop scheduled by the previous
// modal instance, instead of that stale timer killing the new session.
let pendingInspectorStop = null;

// Resolve the element under a device-pixel point. Among all elements whose bounds
// contain the point, prefer ones that actually carry a usable locator (an
// accessibility-id / content-desc or an app resource-id) — so a click lands on the
// meaningful element instead of a nested text/icon with no good locator. Within
// that set pick the tightest (smallest-area) box, breaking ties by draw order
// (higher `z` = drawn on top, e.g. a floating button over the list behind it).
const hasUsableLocator = (el) =>
    !!(el.content_desc || (el.resource_id && !el.resource_id.startsWith('android:')));

const elementAtPoint = (elements, dx, dy) => {
    const hits = [];
    for (const el of elements || []) {
        const b = el.bounds;
        if (dx >= b.x && dx <= b.x + b.width && dy >= b.y && dy <= b.y + b.height) {
            hits.push(el);
        }
    }
    if (!hits.length) return null;
    const preferred = hits.some(hasUsableLocator) ? hits.filter(hasUsableLocator) : hits;
    preferred.sort(
        (a, b) => (a.bounds.area - b.bounds.area) || ((b.z || 0) - (a.z || 0)),
    );
    return preferred[0];
};

/**
 * Interactive Appium inspector: shows the live device screen, lets the user
 * click an element to capture its locator, or switch to "Interact" mode to tap
 * through the app. Picked locators are handed back via onPick().
 */
export const MobileInspectorModal = ({ pdfId, appPackage, appActivity, onPick, onClose }) => {
    const [phase, setPhase] = useState('starting'); // starting | ready | error
    const [statusNote, setStatusNote] = useState('Booting the emulator and Appium session…');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState('pick'); // pick | interact
    const [snap, setSnap] = useState(null); // { screenshot, window_size, elements }
    const [hovered, setHovered] = useState(null);
    const [selected, setSelected] = useState(null);
    const [chosenIdx, setChosenIdx] = useState(0);
    const [locName, setLocName] = useState('');
    const [added, setAdded] = useState([]); // names added this session (for feedback)
    const [typedText, setTypedText] = useState(''); // Interact-mode text to type into the focused field

    const overlayRef = useRef(null);
    const startedRef = useRef(false);
    const dragStartRef = useRef(null);      // mouse-down point (for drag-to-scroll)
    const suppressClickRef = useRef(false); // set when a drag happened, to skip the click
    const moveRafRef = useRef(0);           // rAF handle to throttle hover updates
    const lastMoveRef = useRef(null);       // latest cursor position (for the rAF)

    const win = snap?.window_size || { width: 0, height: 0 };

    // Every inspector call gets a hard timeout. Without it, a single slow/hung
    // request to the device leaves `busy` stuck true forever — which disables every
    // toolbar button AND makes mirror taps no-ops (handleClick returns on `busy`).
    // Aborting after 30s lets the action's `finally` clear `busy` so the UI recovers.
    const REQUEST_TIMEOUT_MS = 30000;

    const request = useCallback(async (endpoint, body) => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const res = await apiFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pdf_id: pdfId, ...(body || {}) }),
                signal: controller.signal,
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `Request failed (${res.status})`);
            }
            return res.json();
        } catch (err) {
            if (err.name === 'AbortError') throw new Error('The device took too long to respond — try that action again.');
            throw err;
        } finally {
            clearTimeout(timer);
        }
    }, [pdfId]);

    const loadSnapshot = useCallback(async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const res = await apiFetch(API_ENDPOINTS.APPIUM_INSPECTOR_SNAPSHOT, { method: 'GET', signal: controller.signal });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `Snapshot failed (${res.status})`);
            }
            const data = await res.json();
            setSnap(data);
            setSelected(null);
            setHovered(null);
            return data;
        } catch (err) {
            if (err.name === 'AbortError') throw new Error('The device took too long to respond — click Refresh.');
            throw err;
        } finally {
            clearTimeout(timer);
        }
    }, []);

    // Start the session on mount; tear it down (debounced) on unmount. The debounce
    // is what makes this survive React 18 StrictMode's dev double-invoke: that fires
    // mount → unmount → mount instantly, so a plain unmount-stop would kill the
    // session we just opened and leave the modal stuck on "Booting…". Instead we
    // schedule the stop and cancel it if we remount within the window (the backend
    // reuses a live session, so the remount is a no-op).
    useEffect(() => {
        // Cancel a teardown scheduled by a previous instance (module-level), so a
        // remount/reopen keeps the live session instead of it being killed.
        if (pendingInspectorStop) {
            clearTimeout(pendingInspectorStop);
            pendingInspectorStop = null;
        }
        if (startedRef.current) return undefined;
        startedRef.current = true;
        (async () => {
            try {
                await request(API_ENDPOINTS.APPIUM_INSPECTOR_START, {
                    app_package: appPackage || undefined,
                    app_activity: appActivity || undefined,
                });
                // Open on a CLEAN, empty LOGIN screen — logged out, nothing pre-filled —
                // so you always start from scratch and drive the app yourself (log in,
                // or tap Register to reach the Register page). reset-app does a
                // `pm clear` + relaunch, which lands on the fresh, empty login page.
                setStatusNote('Opening a fresh login screen…');
                try {
                    const data = await request(API_ENDPOINTS.APPIUM_INSPECTOR_RESET, {});
                    setSnap(data);
                } catch {
                    await loadSnapshot(); // fall back to whatever screen is showing
                }
                setPhase('ready');
            } catch (err) {
                setError(err.message || 'The mobile inspector could not start. Confirm Appium and the Android device are available, then try again.');
                setPhase('error');
            }
        })();
        return () => {
            startedRef.current = false;
            pendingInspectorStop = setTimeout(() => {
                pendingInspectorStop = null;
                apiFetch(API_ENDPOINTS.APPIUM_INSPECTOR_STOP, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pdf_id: pdfId }),
                }).catch(() => {});
            }, 600);
        };
    }, [appPackage, appActivity, loadSnapshot, request, pdfId]);

    useEffect(() => {
        const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [onClose]);

    // Attach the wheel-to-scroll listener natively with { passive: false } so
    // preventDefault() actually stops the modal from scrolling. (React's onWheel
    // is passive, so preventDefault there is ignored.) A ref holds the latest
    // handler so we attach once the device screen is mounted.
    const handleWheelRef = useRef(null);
    useEffect(() => {
        const el = overlayRef.current;
        if (phase !== 'ready' || !el) return undefined;
        const fn = (e) => handleWheelRef.current?.(e);
        el.addEventListener('wheel', fn, { passive: false });
        return () => el.removeEventListener('wheel', fn);
    }, [phase]);

    const toDevicePoint = (e) => {
        const rect = overlayRef.current?.getBoundingClientRect();
        if (!rect || !win.width) return null;
        const fx = (e.clientX - rect.left) / rect.width;
        const fy = (e.clientY - rect.top) / rect.height;
        return { x: Math.round(fx * win.width), y: Math.round(fy * win.height) };
    };

    // Throttled to one update per animation frame so hover tracking stays smooth
    // even when the cursor fires hundreds of mousemove events per second.
    const handleMove = (e) => {
        if (!snap || mode !== 'pick') return;
        lastMoveRef.current = { clientX: e.clientX, clientY: e.clientY };
        if (moveRafRef.current) return;
        moveRafRef.current = requestAnimationFrame(() => {
            moveRafRef.current = 0;
            const m = lastMoveRef.current;
            const rect = overlayRef.current?.getBoundingClientRect();
            if (!rect || !win.width || !m) return;
            const x = Math.round(((m.clientX - rect.left) / rect.width) * win.width);
            const y = Math.round(((m.clientY - rect.top) / rect.height) * win.height);
            setHovered(elementAtPoint(snap.elements, x, y));
        });
    };

    const handleClick = async (e) => {
        if (!snap || busy) return;
        // If this "click" was the end of a drag-to-scroll, don't also tap/pick.
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        const p = toDevicePoint(e);
        if (!p) return;
        if (mode === 'pick') {
            const el = elementAtPoint(snap.elements, p.x, p.y);
            if (el) {
                setSelected(el);
                setChosenIdx(0);
                setLocName(el.suggested_name || '');
            }
            return;
        }
        // interact mode: tap the device, then refresh
        setBusy(true);
        setError(null);
        try {
            const data = await withSession(() => request(API_ENDPOINTS.APPIUM_INSPECTOR_TAP, { x: p.x, y: p.y }));
            setSnap(data);
            setSelected(null);
            setHovered(null);
        } catch (err) {
            setError(err.message || 'The tap could not be sent to the device. Confirm the inspector session is active, then try again.');
        } finally {
            setBusy(false);
        }
    };

    const restartSession = useCallback(async () => {
        await request(API_ENDPOINTS.APPIUM_INSPECTOR_START, {
            app_package: appPackage || undefined,
            app_activity: appActivity || undefined,
        });
    }, [request, appPackage, appActivity]);

    // Run an inspector call; if the session was lost (timed out, cleared, etc.)
    // restart it once and retry, so actions self-heal instead of erroring.
    const withSession = useCallback(async (fn) => {
        try {
            return await fn();
        } catch (err) {
            if (/no inspector session|session is active|no such session|invalid session/i.test(err?.message || '')) {
                await restartSession();
                return await fn();
            }
            throw err;
        }
    }, [restartSession]);

    const doAction = async (endpoint) => {
        setBusy(true);
        setError(null);
        try {
            const data = await withSession(() => (
                endpoint === API_ENDPOINTS.APPIUM_INSPECTOR_SNAPSHOT
                    ? loadSnapshot()
                    : request(endpoint)
            ));
            setSnap(data);
            setSelected(null);
            setHovered(null);
        } catch (err) {
            setError(err.message || 'The inspector action could not be completed. Confirm the device is connected and the inspector session is active, then try again.');
        } finally {
            setBusy(false);
        }
    };

    // Fill the login fields with valid test credentials. `submit=false` keeps the
    // login page visible (filled) so you can pick its locators; tap Login yourself
    // (Interact) to actually log in.
    const runLogin = async (submit) => {
        if (busy || phase !== 'ready') return;
        setBusy(true);
        setError(null);
        try {
            const data = await withSession(() => request(API_ENDPOINTS.APPIUM_INSPECTOR_LOGIN, { submit }));
            setSnap(data);
            setSelected(null);
            setHovered(null);
        } catch (err) {
            setError(err.message || 'The saved login could not be applied. Check the test credentials and confirm the app is on its login screen.');
        } finally {
            setBusy(false);
        }
    };

    // Interact mode: type the box's text into whatever field is currently focused.
    const sendText = async () => {
        if (!typedText || busy || phase !== 'ready') return;
        setBusy(true);
        setError(null);
        try {
            const data = await withSession(() => request(API_ENDPOINTS.APPIUM_INSPECTOR_INPUT_TEXT, { text: typedText }));
            setSnap(data);
            setSelected(null);
            setHovered(null);
            setTypedText('');
        } catch (err) {
            setError(err.message || 'Text could not be entered. Select an editable field on the device, then try again.');
        } finally {
            setBusy(false);
        }
    };

    // Swipe/scroll the device, then refresh. Throttled by `busy` (rapid wheel
    // ticks while a swipe is in flight are ignored).
    const doSwipe = async (x1, y1, x2, y2) => {
        if (busy || phase !== 'ready') return;
        setBusy(true);
        setError(null);
        try {
            const data = await withSession(() => request(API_ENDPOINTS.APPIUM_INSPECTOR_SWIPE, {
                x1: Math.round(x1), y1: Math.round(y1), x2: Math.round(x2), y2: Math.round(y2), duration: 180,
            }));
            setSnap(data);
            setSelected(null);
            setHovered(null);
        } catch (err) {
            setError(err.message || 'The device screen could not be scrolled. Confirm the inspector session is active, then try again.');
        } finally {
            setBusy(false);
        }
    };

    // Mouse wheel over the screen scrolls the app (wheel down = page down). Swipes
    // ~half the screen per notch so you can move top→bottom in a few scrolls.
    const handleWheel = (e) => {
        if (phase !== 'ready' || !win.height) return;
        e.preventDefault();          // don't scroll the modal container instead
        if (busy) return;            // a swipe is already in flight
        const cx = Math.round(win.width / 2);
        const down = e.deltaY > 0;
        const y1 = down ? Math.round(win.height * 0.72) : Math.round(win.height * 0.28);
        const y2 = down ? Math.round(win.height * 0.28) : Math.round(win.height * 0.72);
        doSwipe(cx, y1, cx, y2);
    };
    handleWheelRef.current = handleWheel;

    // Press-drag on the screen = a swipe (like dragging on the emulator).
    const handleMouseDown = (e) => {
        if (phase !== 'ready') return;
        dragStartRef.current = { p: toDevicePoint(e), cx: e.clientX, cy: e.clientY };
    };
    const handleMouseUp = (e) => {
        const start = dragStartRef.current;
        dragStartRef.current = null;
        if (!start || !start.p) return;
        const dist = Math.hypot(e.clientX - start.cx, e.clientY - start.cy);
        // Only treat as a scroll-swipe if the pointer moved a deliberate amount.
        // 12px was far too sensitive — a normal click drifts more than that, so
        // button taps (e.g. "Register", the + FAB) were being misread as swipes
        // and suppressed. 28px cleanly separates an intentional drag from a click.
        if (dist > 28) {
            const end = toDevicePoint(e);
            if (end) {
                suppressClickRef.current = true; // this was a drag — don't pick/tap
                doSwipe(start.p.x, start.p.y, end.x, end.y);
            }
        }
    };

    const chosenLocator = selected?.locators?.[chosenIdx] || null;

    const handleAdd = () => {
        if (!chosenLocator) return;
        onPick?.({
            name: (locName || selected.suggested_name || 'element').trim(),
            strategy: chosenLocator.strategy,
            value: chosenLocator.value,
        });
        setAdded((prev) => [...prev, locName || selected.suggested_name]);
        setSelected(null);
        setHovered(null);
    };

    const highlight = selected || hovered;
    const highlightStyle = useMemo(() => {
        if (!highlight || !win.width || !win.height) return null;
        const b = highlight.bounds;
        return {
            left: `${(b.x / win.width) * 100}%`,
            top: `${(b.y / win.height) * 100}%`,
            width: `${(b.width / win.width) * 100}%`,
            height: `${(b.height / win.height) * 100}%`,
        };
    }, [highlight, win.width, win.height]);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
            <div className="flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 text-indigo-600" />
                        <h3 className="text-sm font-semibold text-slate-900">Pick locators from device</h3>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2">
                    <div className="inline-flex overflow-hidden rounded-lg border border-slate-300">
                        <button
                            type="button"
                            onClick={() => setMode('pick')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${mode === 'pick' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        >
                            <Crosshair className="h-3.5 w-3.5" /> Pick
                        </button>
                        <button
                            type="button"
                            onClick={() => { setMode('interact'); setHovered(null); }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${mode === 'interact' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        >
                            <Hand className="h-3.5 w-3.5" /> Interact
                        </button>
                    </div>
                    <button type="button" disabled={busy || phase !== 'ready'} onClick={() => doAction(API_ENDPOINTS.APPIUM_INSPECTOR_BACK)} className="secondary-btn px-2.5 py-1.5 text-xs disabled:opacity-50">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </button>
                    <button type="button" disabled={busy || phase !== 'ready'} onClick={() => doAction(API_ENDPOINTS.APPIUM_INSPECTOR_SNAPSHOT)} className="secondary-btn px-2.5 py-1.5 text-xs disabled:opacity-50">
                        <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                    <button
                        type="button"
                        disabled={busy || phase !== 'ready'}
                        onClick={() => doAction(API_ENDPOINTS.APPIUM_INSPECTOR_RESET)}
                        title="Log out and relaunch the app so the login screen (email/password) is shown"
                        className="secondary-btn px-2.5 py-1.5 text-xs disabled:opacity-50"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Fresh start (login)
                    </button>
                    <button
                        type="button"
                        disabled={busy || phase !== 'ready'}
                        onClick={() => runLogin(false)}
                        title="Fill valid test credentials (testuser@example.com / Test@1234) WITHOUT submitting — the login page stays visible"
                        className="secondary-btn px-2.5 py-1.5 text-xs disabled:opacity-50"
                    >
                        <Keyboard className="h-3.5 w-3.5" /> Fill login
                    </button>
                    <button
                        type="button"
                        disabled={busy || phase !== 'ready'}
                        onClick={() => runLogin(true)}
                        title="Fill valid test credentials and tap Login"
                        className="secondary-btn px-2.5 py-1.5 text-xs disabled:opacity-50"
                    >
                        <LogIn className="h-3.5 w-3.5" /> Log in
                    </button>
                    <span className="ml-auto text-xs text-slate-500">
                        {mode === 'pick' ? 'Click an element to capture its locator' : 'Tap a field, then type below • use it like a real device'}
                        {snap?.elements ? ` • ${snap.elements.length} elements` : ''}
                    </span>
                </div>

                {/* Interact-mode keyboard bar — drive the app like a real device. */}
                {mode === 'interact' && (
                    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-5 py-2">
                        <Keyboard className="h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            value={typedText}
                            onChange={(e) => setTypedText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') sendText(); }}
                            disabled={busy || phase !== 'ready'}
                            placeholder="Tap a field on the screen, then type its value here…"
                            className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs focus:border-indigo-400 focus:outline-none disabled:opacity-50"
                        />
                        <button
                            type="button"
                            disabled={busy || phase !== 'ready' || !typedText}
                            onClick={sendText}
                            className="primary-btn px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                            <Keyboard className="h-3.5 w-3.5" /> Type
                        </button>
                        <button
                            type="button"
                            disabled={busy || phase !== 'ready'}
                            onClick={() => doAction(API_ENDPOINTS.APPIUM_INSPECTOR_CLEAR)}
                            title="Clear the focused field"
                            className="secondary-btn px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                            <Eraser className="h-3.5 w-3.5" /> Clear
                        </button>
                        <button
                            type="button"
                            disabled={busy || phase !== 'ready'}
                            onClick={() => doAction(API_ENDPOINTS.APPIUM_INSPECTOR_HIDE_KEYBOARD)}
                            title="Hide the on-screen keyboard so it doesn't cover the screen"
                            className="secondary-btn px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                            <ChevronDown className="h-3.5 w-3.5" /> Hide keyboard
                        </button>
                    </div>
                )}

                {/* Body */}
                <div className="flex min-h-0 flex-1">
                    {/* Device screen */}
                    <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-slate-100 p-4">
                        {phase === 'starting' && (
                            <div className="flex flex-col items-center gap-3 text-slate-600">
                                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                                <p className="text-sm">{statusNote}</p>
                                <p className="text-xs text-slate-400">First launch can take up to a minute.</p>
                            </div>
                        )}
                        {phase === 'error' && (
                            <div className="max-w-md rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
                                {error}
                                <p className="mt-2 text-xs text-red-500">Make sure the emulator/AVD is available and the app is installed.</p>
                            </div>
                        )}
                        {phase === 'ready' && snap?.screenshot && (
                            <div className="relative inline-block max-h-full shadow-lg">
                                <img
                                    src={snap.screenshot}
                                    alt="Device screen"
                                    className="block max-h-[74vh] w-auto select-none rounded-md"
                                    draggable={false}
                                />
                                <div
                                    ref={overlayRef}
                                    className={`absolute inset-0 ${mode === 'pick' ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'}`}
                                    onMouseMove={handleMove}
                                    onMouseLeave={() => { if (mode === 'pick') setHovered(null); dragStartRef.current = null; }}
                                    onMouseDown={handleMouseDown}
                                    onMouseUp={handleMouseUp}
                                    onClick={handleClick}
                                >
                                    {highlightStyle && (
                                        <div
                                            className={`pointer-events-none absolute border-2 ${selected ? 'border-indigo-500 bg-indigo-500/20' : 'border-emerald-400 bg-emerald-400/10'}`}
                                            style={highlightStyle}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Locator panel */}
                    <div className="flex w-80 flex-col border-l border-slate-200">
                        {!selected && (
                            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-slate-500">
                                <Crosshair className="h-8 w-8 text-slate-300" />
                                <p>Click an element on the screen to see its suggested locators.</p>
                                {added.length > 0 && (
                                    <p className="mt-3 text-xs text-emerald-600">Added {added.length} locator{added.length > 1 ? 's' : ''} this session.</p>
                                )}
                            </div>
                        )}
                        {selected && (
                            <div className="flex flex-1 flex-col overflow-y-auto p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Selected element</p>
                                <div className="mt-1 space-y-0.5 text-xs text-slate-600">
                                    <p className="truncate"><span className="text-slate-400">class:</span> {selected.class || '—'}</p>
                                    {selected.text ? <p className="truncate"><span className="text-slate-400">text:</span> {selected.text}</p> : null}
                                    {selected.content_desc ? <p className="truncate"><span className="text-slate-400">desc:</span> {selected.content_desc}</p> : null}
                                    {selected.resource_id ? <p className="truncate"><span className="text-slate-400">id:</span> {selected.resource_id}</p> : null}
                                </div>

                                <label className="mt-4 block text-xs font-medium text-slate-700">Locator name</label>
                                <input
                                    type="text"
                                    value={locName}
                                    onChange={(e) => setLocName(e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                                    placeholder="e.g. login_button"
                                />

                                <p className="mt-4 text-xs font-medium text-slate-700">Choose a locator</p>
                                <div className="mt-1 space-y-1.5">
                                    {selected.locators.map((loc, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setChosenIdx(idx)}
                                            className={`flex w-full flex-col items-start rounded-lg border px-3 py-2 text-left ${chosenIdx === idx ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                                        >
                                            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                                                {STRATEGY_LABELS[loc.strategy] || loc.strategy}
                                                {loc.preferred && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">recommended</span>}
                                            </span>
                                            <span className="mt-0.5 break-all font-mono text-xs text-slate-800">{loc.value}</span>
                                        </button>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={handleAdd}
                                    className="primary-btn mt-4 w-full justify-center py-2 text-sm"
                                >
                                    Add locator to POM
                                </button>
                            </div>
                        )}
                        {error && phase === 'ready' && (
                            <div className="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-600">{error}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default MobileInspectorModal;
